import useFetch from 'hooks/useFetch';
import qs from 'qs';
import { mergeLeft } from 'ramda';
import { useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectAuthenticityToken } from 'state/authenticityToken';
import { selectTenant } from 'state/tenant';
import {
  selectSessionToken,
  loadSessionTokenFromLocalStore,
  saveSessionTokenToLocalStore,
  setSessionToken,
} from 'state/sessionToken';
import { Status } from './requests/fetchUtils';
import parse from 'url-parse';

/**
 * Builder
 */
const getOptions = (
  options: RequestInit | undefined,
  authenticityToken: string,
  sessionToken: string,
  clientName: string,
) => {
  const defaultOptions: RequestInit = {
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
      'X-CSRF-Token': authenticityToken,
      'X-Client-Name': clientName,
      Authorization: 'Bearer ' + sessionToken,
    },
  };
  return mergeLeft(options, defaultOptions);
};

type ApiRequestInit = Omit<RequestInit, 'body'> & {
  body?: object;
};

export type ApiFetch = (
  input: string,
  init: ApiRequestInit,
) => Promise<Response>;

const useFetchApi = (): [Status, ApiFetch] => {
  const [status, fetch] = useFetch();
  const authenticityToken = useSelector(selectAuthenticityToken);
  const { overrideConfig, clientName } = useSelector(selectTenant);
  const dispatch = useDispatch();
  let { token } = useSelector(selectSessionToken);

  const sessionTokenFromLocalStore = loadSessionTokenFromLocalStore();
  if (sessionTokenFromLocalStore) token = sessionTokenFromLocalStore;
  const newFetch: ApiFetch = useCallback(
    async (input, options) => {
      let body = JSON.stringify(options.body);

      if (options.method === undefined || options.method === 'GET') {
        const parsed = parse(input);
        const oldQueryString = ((parsed.query as unknown) as string).replace(
          /^\?/,
          '',
        );
        const oldQuery = qs.parse(oldQueryString);
        const newQuery = { overrideConfig, ...oldQuery };
        parsed.set('query', qs.stringify(newQuery));
        input = parsed.toString();
      } else {
        body = JSON.stringify({ ...options.body, overrideConfig });
      }

      const mergeOptions = {
        ...options,
        body,
      };

      const newOptions = getOptions(
        mergeOptions,
        authenticityToken,
        token,
        clientName,
      );
      const response = fetch(input, newOptions);
      const sessionToken = (await response).headers.get('X-Session-Token');
      if (sessionToken) {
        saveSessionTokenToLocalStore(sessionToken);
        dispatch(setSessionToken(sessionToken));
      }
      return response;
    },
    [authenticityToken, fetch, overrideConfig, token, dispatch, clientName],
  );

  return [status, newFetch];
};

export default useFetchApi;
